import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight } from 'lucide-react';
import { TrainingPlan } from '../../types/athlete';
import { formatRaceDateWithCountdown, getProximityIcon } from '../../lib/utils/dateUtils';

interface RaceCountdownWidgetProps {
  upcomingRaces: TrainingPlan[];
}

const RaceCountdownWidget: React.FC<RaceCountdownWidgetProps> = ({ upcomingRaces }) => {
  if (upcomingRaces.length === 0) {
    return (
      <div className="card-base p-6">
        <div className="flex items-center space-x-3 mb-3">
          <Calendar className="text-[#3b82f6]" size={24} />
          <h2 className="text-xl font-semibold">Upcoming Races</h2>
        </div>
        <p className="text-muted text-sm">
          No upcoming races scheduled. Create a training plan to get started!
        </p>
        <Link
          to="/plans/new"
          className="inline-flex items-center mt-3 text-sm text-[#3b82f6] hover:underline"
        >
          Create a Plan <ChevronRight size={16} className="ml-1" />
        </Link>
      </div>
    );
  }

  return (
    <div className="card-base p-6">
      <div className="flex items-center space-x-3 mb-4">
        <Calendar className="text-[#3b82f6]" size={24} />
        <h2 className="text-xl font-semibold">Upcoming Races</h2>
      </div>
      <div className="space-y-3">
        {upcomingRaces.map((race) => {
          const raceInfo = formatRaceDateWithCountdown(race.end_date);
          const icon = getProximityIcon(raceInfo.proximityStatus);

          return (
            <Link
              key={race.id}
              to={`/plans/${race.id}`}
              className="block p-4 bg-[#1a1f2e] hover:bg-[#2d3748] rounded-lg transition-colors no-underline"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-2xl">{icon}</span>
                    <h3 className="font-semibold text-white">{race.name}</h3>
                  </div>
                  <p className="text-sm text-muted">{raceInfo.formatted}</p>
                </div>
                <div className="text-right ml-4">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${raceInfo.colorClass}`}>
                    {raceInfo.displayText}
                  </div>
                  {race.status === 'draft' && (
                    <p className="text-xs text-muted mt-1">Draft</p>
                  )}
                </div>
              </div>
              {race.description && (
                <p className="text-sm text-muted mt-2 line-clamp-1">
                  {race.description}
                </p>
              )}
            </Link>
          );
        })}
      </div>
      {upcomingRaces.length > 0 && (
        <Link
          to="/plans"
          className="inline-flex items-center mt-4 text-sm text-[#3b82f6] hover:underline"
        >
          View all plans <ChevronRight size={16} className="ml-1" />
        </Link>
      )}
    </div>
  );
};

export default RaceCountdownWidget;
